import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Modal,
  Pagination,
  Table,
} from "react-bootstrap";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import {
  EmptyState,
  ErrorAlert,
  Loading,
  Meter,
  PageHeader,
  PlanLock,
  StatusBadge,
  UpgradeGrid,
  formatDate,
} from "../../layout/secure/common";
import {
  cancelInvitationCampaign,
  getInvitationCampaign,
  listInvitationCampaigns,
  listSurveys,
  messageFromError,
  previewInvitations,
  remindInvitation,
  resendInvitation,
  sendInvitations,
} from "../../utils/api";
import { parseRecipientCsv, parseRecipientText } from "./invitation-utils";
import useUsage from "./use-usage";

const EMPTY_FORM = {
  surveyId: "",
  name: "",
  recipientsText: "",
  subject: "",
  message: "",
  scheduledAt: "",
};

function Invitations() {
  const [searchParams] = useSearchParams();
  const preferredSurveyId = searchParams.get("survey") || "";
  const { data: usage, error: usageError, load: loadUsage } = useUsage();
  const enabled = Boolean(usage?.features?.invitations && usage?.requests?.enabled);
  const [surveys, setSurveys] = useState([]);
  const [campaigns, setCampaigns] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [recipientErrors, setRecipientErrors] = useState([]);
  const [preview, setPreview] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState(uuidv4());

  const loadWorkspace = useCallback(async () => {
    if (!enabled) return;
    setError("");
    try {
      const [surveyData, campaignData] = await Promise.all([
        listSurveys({ status: "published", page: 1, limit: 100 }),
        listInvitationCampaigns({ page, limit: 20 }),
      ]);
      const published = (surveyData.items || []).filter(item =>
        item.status === "published" && item.acceptingResponses !== false
      );
      setSurveys(published);
      setCampaigns(campaignData.items || []);
      setPagination(campaignData.pagination || null);
      setForm(current => ({
        ...current,
        surveyId: current.surveyId
          || published.find(item => item.uuid === preferredSurveyId)?.uuid
          || published[0]?.uuid
          || "",
      }));
    } catch (requestError) {
      setError(messageFromError(requestError, "Invitation delivery could not be loaded."));
    }
  }, [enabled, page, preferredSurveyId]);

  useEffect(() => { loadWorkspace(); }, [loadWorkspace]);

  const parsed = useMemo(
    () => parseRecipientText(form.recipientsText),
    [form.recipientsText]
  );

  const payload = () => ({
    surveyId: form.surveyId,
    name: form.name || "Survey invitation",
    subject: form.subject,
    message: form.message,
    scheduledAt: form.scheduledAt
      ? new Date(form.scheduledAt).toISOString()
      : null,
    recipients: parsed.recipients,
  });

  const validate = () => {
    const errors = [...parsed.errors];
    if (!form.surveyId) errors.push("Choose a published survey.");
    if (!form.subject.trim()) errors.push("Enter an email subject.");
    if (!form.message.trim()) errors.push("Enter an invitation message.");
    if (!parsed.recipients.length) errors.push("Add at least one valid recipient.");
    setRecipientErrors(errors);
    return !errors.length;
  };

  const openPreview = async () => {
    if (!validate()) return;
    setBusy("preview");
    setError("");
    try {
      const result = await previewInvitations(payload());
      setPreview(result.preview);
    } catch (requestError) {
      setError(messageFromError(requestError, "The invitation preview could not be created."));
    } finally {
      setBusy("");
    }
  };

  const send = async () => {
    if (!validate()) return;
    setBusy("send");
    setError("");
    try {
      const result = await sendInvitations(payload(), idempotencyKey);
      toast.success(
        result.duplicate
          ? "This invitation batch was already accepted."
          : `${result.requestUnits} invitation${result.requestUnits === 1 ? "" : "s"} queued.`
      );
      setPreview(null);
      setForm(current => ({
        ...EMPTY_FORM,
        surveyId: current.surveyId,
      }));
      setIdempotencyKey(uuidv4());
      await Promise.all([loadWorkspace(), loadUsage()]);
    } catch (requestError) {
      setError(messageFromError(requestError, "The invitation batch could not be sent."));
    } finally {
      setBusy("");
    }
  };

  const upload = async event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > 1024 * 1024) {
      setRecipientErrors(["Recipient CSV files must be 1 MB or smaller."]);
      return;
    }
    const result = parseRecipientCsv(await file.text());
    if (result.errors.length) {
      setRecipientErrors(result.errors);
      return;
    }
    setRecipientErrors([]);
    setForm(current => ({
      ...current,
      recipientsText: result.recipients
        .map(item => item.displayName
          ? `"${item.displayName.replace(/"/g, "\"\"")}",${item.email}`
          : item.email)
        .join("\n"),
    }));
  };

  const openDetail = async campaignId => {
    setBusy(campaignId);
    setError("");
    try {
      const result = await getInvitationCampaign(campaignId);
      setDetail(result.item);
    } catch (requestError) {
      setError(messageFromError(requestError, "Invitation details could not be loaded."));
    } finally {
      setBusy("");
    }
  };

  const recipientAction = async (invitation, action) => {
    setBusy(invitation.id);
    setError("");
    try {
      if (action === "remind") await remindInvitation(invitation.id);
      else await resendInvitation(invitation.id);
      toast.success(action === "remind" ? "Reminder queued." : "Invitation queued again.");
      const refreshed = await getInvitationCampaign(detail.id);
      setDetail(refreshed.item);
      await loadWorkspace();
    } catch (requestError) {
      setError(messageFromError(requestError, `The invitation could not be ${action === "remind" ? "reminded" : "resent"}.`));
    } finally {
      setBusy("");
    }
  };

  const cancelCampaign = async campaign => {
    setBusy(campaign.id);
    setError("");
    try {
      await cancelInvitationCampaign(campaign.id);
      toast.success("Scheduled invitation campaign cancelled.");
      await loadWorkspace();
    } catch (requestError) {
      setError(messageFromError(requestError, "The campaign could not be cancelled."));
    } finally {
      setBusy("");
    }
  };

  if (!usage && !usageError) return <Loading label="Loading invitation access" />;
  if (enabled && campaigns === null && !error) return <Loading label="Loading invitations" />;

  const requests = usage?.requests || {};
  const remaining = requests.remaining;
  const overRemaining = remaining !== null
    && remaining !== undefined
    && parsed.recipients.length > Number(remaining);

  return (
    <>
      <PageHeader
        title="Invitations"
        description="Send a published survey to named recipients and track each delivery."
        action={!enabled ? <PlanLock>Paid feature</PlanLock> : null}
      />
      <ErrorAlert error={usageError || error} onRetry={() => Promise.all([loadUsage(), loadWorkspace()])} />
      {!enabled ? (
        <>
          <Alert variant="info">
            Generic survey links remain available on your plan. Direct recipient invitations require Starter, Complete, or Beyond.
          </Alert>
          <section className="fera-panel">
            <h2>Collect responses with a generic link</h2>
            <p className="text-muted mb-0">Create a secure, rotatable link from any published survey. Copying and sharing that link consumes no request usage.</p>
          </section>
          <UpgradeGrid access={usage?.access} title="Add direct survey invitations" />
        </>
      ) : (
        <div className="fera-invitation-layout">
          <section className="fera-panel" aria-labelledby="send-invitations-heading">
            <div className="fera-panel-heading">
              <div>
                <p className="fera-eyebrow">New campaign</p>
                <h2 id="send-invitations-heading">Send invitations</h2>
              </div>
              <div className="fera-compact-meter">
                <Meter
                  label="Requests this period"
                  value={requests.used || 0}
                  maximum={requests.limit}
                />
              </div>
            </div>
            {!surveys.length ? (
              <EmptyState
                title="Publish a survey first"
                detail="Invitations can only be sent for a published survey that is accepting responses."
                icon="email"
              />
            ) : (
              <Form onSubmit={event => { event.preventDefault(); openPreview(); }}>
                <div className="fera-form-grid">
                  <Form.Group>
                    <Form.Label htmlFor="invitation-survey">Survey</Form.Label>
                    <Form.Select
                      id="invitation-survey"
                      value={form.surveyId}
                      onChange={event => setForm({ ...form, surveyId: event.target.value })}
                    >
                      {surveys.map(survey => (
                        <option key={survey.uuid} value={survey.uuid}>{survey.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label htmlFor="invitation-name">Campaign name</Form.Label>
                    <Form.Control
                      id="invitation-name"
                      value={form.name}
                      maxLength={150}
                      placeholder="July client survey"
                      onChange={event => setForm({ ...form, name: event.target.value })}
                    />
                  </Form.Group>
                </div>
                <Form.Group className="mt-3">
                  <div className="d-flex align-items-center justify-content-between gap-3">
                    <Form.Label htmlFor="invitation-recipients" className="mb-0">Recipients</Form.Label>
                    <Button as="label" htmlFor="invitation-csv" size="sm" variant="outline-primary">
                      Import CSV
                    </Button>
                    <Form.Control
                      id="invitation-csv"
                      className="visually-hidden"
                      type="file"
                      accept=".csv,text/csv"
                      onChange={upload}
                    />
                  </div>
                  <Form.Control
                    id="invitation-recipients"
                    as="textarea"
                    rows={6}
                    value={form.recipientsText}
                    placeholder={"alex@example.com\n\"Alex Morgan\",alex.morgan@example.com"}
                    onChange={event => {
                      setForm({ ...form, recipientsText: event.target.value });
                      setRecipientErrors([]);
                    }}
                    aria-describedby="recipient-help"
                  />
                  <Form.Text id="recipient-help">
                    One recipient per line as email or name,email. CSV files need an email column and may include a name column.
                  </Form.Text>
                </Form.Group>
                <div className="fera-form-grid mt-3">
                  <Form.Group>
                    <Form.Label htmlFor="invitation-subject">Subject</Form.Label>
                    <Form.Control
                      id="invitation-subject"
                      value={form.subject}
                      maxLength={200}
                      onChange={event => setForm({ ...form, subject: event.target.value })}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label htmlFor="invitation-schedule">Send later (optional)</Form.Label>
                    <Form.Control
                      id="invitation-schedule"
                      type="datetime-local"
                      value={form.scheduledAt}
                      onChange={event => setForm({ ...form, scheduledAt: event.target.value })}
                    />
                  </Form.Group>
                </div>
                <Form.Group className="mt-3">
                  <Form.Label htmlFor="invitation-message">Message</Form.Label>
                  <Form.Control
                    id="invitation-message"
                    as="textarea"
                    rows={5}
                    maxLength={20000}
                    value={form.message}
                    onChange={event => setForm({ ...form, message: event.target.value })}
                  />
                </Form.Group>
                {recipientErrors.length ? (
                  <Alert variant="danger" className="mt-3 mb-0">
                    <ul className="mb-0">
                      {recipientErrors.slice(0, 12).map(item => <li key={item}>{item}</li>)}
                    </ul>
                  </Alert>
                ) : null}
                {overRemaining ? (
                  <Alert variant="warning" className="mt-3 mb-0">
                    This batch has {parsed.recipients.length} recipients, but only {remaining} request units remain. The full batch will be rejected.
                  </Alert>
                ) : null}
                <div className="fera-invitation-submit">
                  <span>{parsed.recipients.length} request unit{parsed.recipients.length === 1 ? "" : "s"}</span>
                  <Button type="submit" disabled={Boolean(busy) || overRemaining}>
                    {busy === "preview" ? "Preparing…" : "Preview invitation"}
                  </Button>
                </div>
              </Form>
            )}
          </section>

          <section className="fera-panel" aria-labelledby="campaigns-heading">
            <div className="fera-panel-heading">
              <div>
                <p className="fera-eyebrow">Delivery</p>
                <h2 id="campaigns-heading">Invitation campaigns</h2>
              </div>
            </div>
            {!campaigns?.length ? (
              <EmptyState
                title="No invitations sent"
                detail="Preview a message, confirm its recipient count, and send your first campaign."
                icon="email"
              />
            ) : (
              <div className="table-responsive">
                <Table className="fera-table" hover>
                  <thead>
                    <tr>
                      <th>Campaign</th>
                      <th>Status</th>
                      <th>Delivery</th>
                      <th>Created</th>
                      <th><span className="visually-hidden">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map(campaign => (
                      <tr key={campaign.id}>
                        <td><strong>{campaign.name}</strong><small className="d-block text-muted">{campaign.subject}</small></td>
                        <td><StatusBadge status={campaign.status} /></td>
                        <td>{campaign.sentCount} sent · {campaign.failedCount} failed · {campaign.recipientCount} total</td>
                        <td>{formatDate(campaign.createdAt)}</td>
                        <td className="text-end">
                          <div className="fera-row-actions">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={busy === campaign.id}
                              onClick={() => openDetail(campaign.id)}
                            >
                              Details
                            </Button>
                            {["scheduled", "queued"].includes(campaign.status) ? (
                              <Button
                                size="sm"
                                variant="outline-danger"
                                disabled={busy === campaign.id}
                                onClick={() => cancelCampaign(campaign)}
                              >
                                Cancel
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
            {(pagination?.pages || 1) > 1 ? (
              <Pagination className="fera-pagination">
                <Pagination.Prev disabled={page <= 1} onClick={() => setPage(value => value - 1)} />
                <Pagination.Item active>{page}</Pagination.Item>
                <Pagination.Next disabled={page >= pagination.pages} onClick={() => setPage(value => value + 1)} />
              </Pagination>
            ) : null}
          </section>
        </div>
      )}

      <Modal show={Boolean(preview)} onHide={() => setPreview(null)} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Preview invitation</Modal.Title></Modal.Header>
        <Modal.Body>
          <dl className="fera-preview-summary">
            <div><dt>Survey</dt><dd>{preview?.survey?.name}</dd></div>
            <div><dt>Recipients</dt><dd>{preview?.requestUnits} request units</dd></div>
            <div><dt>Delivery</dt><dd>{preview?.scheduledAt ? new Date(preview.scheduledAt).toLocaleString() : "Send now"}</dd></div>
          </dl>
          <article className="fera-email-preview">
            <strong>{preview?.subject}</strong>
            <p>{preview?.message}</p>
            <Button disabled>Open survey</Button>
          </article>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPreview(null)}>Edit</Button>
          <Button onClick={send} disabled={busy === "send"}>
            {busy === "send" ? "Queuing…" : `Send to ${preview?.requestUnits || 0}`}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(detail)} onHide={() => setDetail(null)} size="xl" centered>
        <Modal.Header closeButton><Modal.Title>{detail?.name}</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="text-muted">{detail?.subject}</p>
          <div className="table-responsive">
            <Table className="fera-table" hover>
              <thead><tr><th>Recipient</th><th>Status</th><th>Last activity</th><th><span className="visually-hidden">Actions</span></th></tr></thead>
              <tbody>
                {(detail?.invitations || []).map(invitation => (
                  <tr key={invitation.id}>
                    <td><strong>{invitation.displayName || invitation.email}</strong>{invitation.displayName ? <small className="d-block text-muted">{invitation.email}</small> : null}</td>
                    <td><StatusBadge status={invitation.status} /></td>
                    <td>{formatDate(invitation.submittedAt || invitation.startedAt || invitation.openedAt || invitation.sentAt || invitation.updatedAt)}</td>
                    <td className="text-end">
                      <div className="fera-row-actions">
                        {["sent", "opened", "started"].includes(invitation.status) ? (
                          <Button size="sm" variant="outline-primary" disabled={busy === invitation.id} onClick={() => recipientAction(invitation, "remind")}>Remind</Button>
                        ) : null}
                        {["failed", "dead-lettered"].includes(invitation.status) ? (
                          <Button size="sm" variant="outline-primary" disabled={busy === invitation.id} onClick={() => recipientAction(invitation, "resend")}>Resend</Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer><Button variant="outline-secondary" onClick={() => setDetail(null)}>Close</Button></Modal.Footer>
      </Modal>
    </>
  );
}

export default Invitations;
